import { useEffect, useRef, useState, type ReactNode } from "react";

import { QueryErrorState } from "@/api/QueryErrorState";
import { QueryPendingState } from "@/api/QueryPendingState";
import {
  PROJECT_HISTORY_INITIAL_COUNT,
  queryKeys,
  useProjectHistoryQuery,
  useProjectOverviewQuery,
  type DraftSummary,
  type ProjectHistoryEvent,
  type ProjectHistoryTechnical,
} from "@/api/hooks";
import { isTerminalQueryError } from "@/api/queryClient";
import { useSession } from "@/auth/SessionContext";
import { EM_DASH, formatDateTime } from "@/lib/format";
import {
  historyActorPhrase,
  historyEventLabel,
  historyOutcomeLabel,
} from "@/lib/history";
import { ProjectJourney } from "@/ui/project/ProjectJourney";
import { Badge } from "@/ui/Badge";
import { Button } from "@/ui/Button";
import { Card, CardBody, CardHeader, CardTitle, SectionLabel } from "@/ui/Card";
import { PublicationLane } from "@/ui/publication/PublicationLane";
import { StaleDataNotice } from "@/ui/states";

import { useProjectId } from "./ProjectLayoutRoute";
import { ProjectPreviewDialog } from "./ProjectPreviewDialog";
import { ProjectSubmitDialog } from "./ProjectSubmitDialog";

function DefinitionRow({
  term,
  children,
}: {
  term: string;
  children: ReactNode;
}) {
  return (
    <div className="flex flex-wrap items-baseline justify-between gap-2 py-2">
      <dt className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {term}
      </dt>
      <dd className="text-sm text-foreground">{children}</dd>
    </div>
  );
}

/**
 * Draft-axis labels, each exhaustive over its contract union.
 *
 * `satisfies` rather than `Record<string, …>`: the previous loose keying let a
 * label map keep members long after #27 narrowed the union it described, and
 * would have let a new member arrive unlabelled. Now a contract enum change
 * fails the build here.
 *
 * The draft's own promotion axis is deliberately no longer rendered on this
 * card. The promotion card below owns that ground with both of #27's axes, and
 * a single-axis copy beside it is how a surface starts implying that a recorded
 * request is a deployment.
 *
 * `SIGN_OFF_LABEL` left with #651: `DraftSummary.signOffState` has no writer —
 * the contract publishes a permanent null — so the row it fed rendered an
 * em-dash forever while `publicationStatus.candidateAcceptance` carried the
 * real acceptance axis one card up. A row whose only possible value is "—"
 * beside a field that answers the same question is the projection disagreement
 * #650 exists to end, told in furniture.
 */
type ValidationState = NonNullable<DraftSummary["validationState"]>;

const VALIDATION_LABEL = {
  passed: { label: "Passed", tone: "success" },
  failed: { label: "Failed", tone: "warning" },
  not_run: { label: "Not run", tone: "neutral" },
} satisfies Record<
  ValidationState,
  { label: string; tone: "success" | "warning" | "neutral" }
>;

const TECHNICAL_COORDINATE_LABELS = {
  oldPreviewTargetId: "Old preview target",
  newPreviewTargetId: "New preview target",
  contentHash: "Content hash",
  baseContentSha256: "Base content digest",
  archiveSha256: "Archive digest",
  publicationId: "Publication",
  packPrNumber: "Pack PR",
  packPrUrl: "Pack PR URL",
  mergeCommitSha: "Merge commit",
  packName: "Pack",
  archiveOldRevision: "Archive revision (old)",
  archiveNewRevision: "Archive revision (new)",
  classification: "Classification",
} as const satisfies Record<keyof ProjectHistoryTechnical, string>;

/**
 * The technical half of one history entry — the authorized disclosure #438
 * confines every engineering coordinate to, on `DigestDisclosure`'s native
 * `details` pattern (keyboard- and screen-reader-reachable by construction).
 *
 * Coordinates only, rendered verbatim from the bounded `technical` bag plus
 * the entry's own identifiers; never the clinician-facing explanation, and
 * never turned into a link (ADR 0001 forbids identifiers in browser URLs,
 * and a pack PR url is an operational coordinate, not navigation).
 */
function HistoryTechnicalDetails({ event }: { event: ProjectHistoryEvent }) {
  const technical: ProjectHistoryTechnical = event.technical ?? {};
  const candidates: Array<[string, string | number | null | undefined]> = [
    ["Event id", event.id],
    ["Actor subject", event.actor],
    ["Candidate", event.candidateId],
    ...(
      Object.keys(TECHNICAL_COORDINATE_LABELS) as Array<
        keyof typeof TECHNICAL_COORDINATE_LABELS
      >
    ).map((key): [string, string | number | null | undefined] => [
      TECHNICAL_COORDINATE_LABELS[key],
      technical[key],
    ]),
  ];
  // `String(value)` rather than truthiness: a numeric coordinate of 0 (a
  // first archive's old revision) is a recorded value, not an absence.
  const rows = candidates.flatMap(
    ([label, value]): Array<[string, string]> =>
      value === null || value === undefined || value === ""
        ? []
        : [[label, String(value)]],
  );
  return (
    <details className="mt-1">
      <summary
        aria-label={`Show technical details for ${historyEventLabel(event.kind)} at ${formatDateTime(event.occurredAt)}`}
        className="cursor-pointer text-xs text-muted-foreground hover:text-foreground"
      >
        Technical details
      </summary>
      <dl className="mt-1.5 space-y-1 rounded-md border border-border bg-muted/30 p-2.5">
        {rows.map(([label, value]) => (
          <div
            key={label}
            className="flex flex-wrap items-baseline justify-between gap-2"
          >
            <dt className="text-xs text-muted-foreground">{label}</dt>
            <dd>
              <code className="block select-all break-all text-xs text-foreground">
                {value}
              </code>
            </dd>
          </div>
        ))}
      </dl>
    </details>
  );
}

/**
 * The compact Project History (#438), rendered from #432's projection and
 * from nothing else: every row is an immutable evidence record served by
 * `GET .../history`, copied — never inferred, never joined client-side. This
 * card replaced the derived Activity timeline, whose own header promised it
 * would go away "when a real activity resource ships"; #432 shipped it.
 *
 * Collapsed, the card requests and shows exactly the three newest entries.
 * `View all` follows that first page's server cursor and widens the same list;
 * later `Show older entries` presses continue the bounded cursor walk. It is
 * deliberately not a route — #438 forbids a global Audit navigation, and an
 * address is how a card becomes one.
 *
 * Same-candidate entries connect through the shortened candidate identity on
 * each row (the full identity sits in Technical details); the identity is
 * rendered as text only, per ADR 0001.
 */
function ProjectHistoryCard({ projectId }: { projectId: string }) {
  const { session } = useSession();
  const history = useProjectHistoryQuery(projectId);
  const [expanded, setExpanded] = useState(false);

  const events = history.data?.pages.flatMap((page) => page.items) ?? [];
  const shown = expanded
    ? events
    : events.slice(0, PROJECT_HISTORY_INITIAL_COUNT);
  const hasMoreThanCollapsed =
    events.length > PROJECT_HISTORY_INITIAL_COUNT || history.hasNextPage;
  const terminalError = history.isError && isTerminalQueryError(history.error);
  const paginationEndRef = useRef<HTMLSpanElement>(null);
  const focusPaginationEndAfterFetch = useRef(false);

  useEffect(() => {
    if (focusPaginationEndAfterFetch.current && !history.isFetchingNextPage) {
      focusPaginationEndAfterFetch.current = false;
      if (!history.isFetchNextPageError && !history.hasNextPage) {
        paginationEndRef.current?.focus();
      }
    }
  }, [
    history.hasNextPage,
    history.isFetchingNextPage,
    history.isFetchNextPageError,
  ]);

  function toggleExpanded() {
    if (expanded) {
      setExpanded(false);
      return;
    }
    setExpanded(true);
    // The initial request contains only the three rows the collapsed card is
    // allowed to load. "View all" therefore follows the server cursor itself;
    // it never reveals a hidden 20-row prefetch.
    if (
      history.data?.pages.length === 1 &&
      history.hasNextPage &&
      !history.isFetchingNextPage
    ) {
      focusPaginationEndAfterFetch.current = false;
      void history.fetchNextPage();
    }
  }

  function fetchOlderEntries() {
    if (!history.hasNextPage || history.isFetchingNextPage) return;
    // If this fetch reaches the end, the button is replaced by the explicit
    // end state. Move focus with that replacement rather than dropping it to
    // the document body (#438's keyboard/focus requirement).
    focusPaginationEndAfterFetch.current = true;
    void history.fetchNextPage();
  }

  return (
    // The right-hand pane of the canonical Overview: a named region — the
    // reference's `History` label with its `Read-only` note beside it — over
    // one compact card. Until the canonical-layout correction this was a
    // full-width card at the foot of the page (`lg:col-span-2`, the retired
    // ActivityCard's slot). The grid in `ProjectOverviewRoute` now places it
    // beside the primary content from `lg` up and beneath it below, in DOM
    // order, so there is ONE instance of this pane at every width — no
    // duplicate markup, no second live region, no second announcement.
    <section aria-labelledby="history-heading" className="min-w-0 space-y-2">
      <div className="flex items-center justify-between gap-3">
        <SectionLabel id="history-heading">History</SectionLabel>
        <span className="text-xs text-muted-foreground">Read-only</span>
      </div>
      <Card>
        <CardBody>
          {history.isPending ? (
            <QueryPendingState
              isPaused={history.isPaused}
              failureReason={history.failureReason}
              label="Loading project history"
              errorTitle="History could not be loaded"
              rows={2}
              queryKey={queryKeys.projectHistory(projectId)}
            />
          ) : terminalError || !history.data ? (
            <QueryErrorState
              error={history.error}
              subject="History entries"
              title="History could not be loaded"
              deniedDescription="You are not authorized to see this project's history."
              onRetry={() => void history.refetch()}
              treatNotFoundAsDenied
            />
          ) : (
            <>
              <StaleDataNotice
                visible={
                  history.isRefetchError && !history.isFetchNextPageError
                }
                updatedAt={history.dataUpdatedAt}
                onRetry={() => void history.refetch()}
                className="mb-3"
              />
              {events.length === 0 ? (
                // Structurally unreachable — creation itself is the first entry —
                // and stated anyway: an empty answer must never render as a
                // loading skeleton or a blank card (#438's explicit-states rule).
                <p className="text-sm text-muted-foreground">
                  No recorded history yet.
                </p>
              ) : (
                <ol className="divide-y divide-border">
                  {shown.map((event) => {
                    const outcome = historyOutcomeLabel(
                      event.kind,
                      event.outcome,
                    );
                    const actor = historyActorPhrase(
                      event.actor,
                      session.user.subject,
                    );
                    return (
                      <li
                        key={event.id}
                        /*
                          Stacked below `sm` — the ActivityCard's measured rule,
                          kept: the timestamp renders 194-198px depending on the
                          run's own digits, and a label sharing its line moves
                          the page height by the minute the visual suite runs.
                          Stacking removes the cause; at `sm` and up the row is
                          the label/timestamp pair it always was.
                        */
                        className="flex flex-col gap-2 py-2 sm:flex-row sm:flex-wrap sm:items-baseline sm:justify-between"
                      >
                        <div className="min-w-0">
                          <p className="break-words text-sm text-foreground">
                            {historyEventLabel(event.kind)}
                            {outcome ? (
                              <span className="text-muted-foreground">
                                {` — ${outcome}`}
                              </span>
                            ) : null}
                            <span className="text-muted-foreground">
                              {event.actor ? ` ${actor}` : ` — ${actor}`}
                            </span>
                          </p>
                          <HistoryTechnicalDetails event={event} />
                        </div>
                        <span className="text-sm text-muted-foreground">
                          {formatDateTime(event.occurredAt)}
                        </span>
                      </li>
                    );
                  })}
                </ol>
              )}
              {hasMoreThanCollapsed || expanded ? (
                <div className="mt-3 flex flex-wrap items-center gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    aria-expanded={expanded}
                    onClick={toggleExpanded}
                  >
                    {expanded ? "Show the newest three" : "View all"}
                  </Button>
                  {expanded && history.hasNextPage ? (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={history.isFetchingNextPage}
                      onClick={fetchOlderEntries}
                    >
                      {history.isFetchingNextPage
                        ? "Loading older entries…"
                        : history.isFetchNextPageError
                          ? "Try loading older entries again"
                          : "Show older entries"}
                    </Button>
                  ) : null}
                  {expanded && history.isFetchNextPageError ? (
                    <span role="alert" className="text-xs text-destructive">
                      Older history could not be loaded.
                    </span>
                  ) : null}
                  {expanded && !history.hasNextPage ? (
                    // The pagination-end state, stated (#438) — absence of a
                    // button is not a statement.
                    <span
                      ref={paginationEndRef}
                      role="status"
                      tabIndex={-1}
                      className="text-xs text-muted-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
                    >
                      End of history.
                    </span>
                  ) : null}
                </div>
              ) : null}
              <p className="mt-3 text-xs text-muted-foreground">
                Each entry is copied from the Editor Service&apos;s immutable
                records. Repository coordinates stay in Technical details.
              </p>
            </>
          )}
        </CardBody>
      </Card>
    </section>
  );
}

/** Current journey first; frozen evidence and immutable History follow. */
export function ProjectOverviewRoute({
  modal,
}: {
  /**
   * The route-backed canonical modal rendered OVER this page (#518).
   * `/preview` and `/submit` are addresses whose answer is the Overview plus
   * the named modal: the same component type renders at the same route
   * position for all three addresses, so opening or closing a modal
   * reconciles this page in place instead of remounting it — which is what
   * keeps the opener's DOM node alive for the dialog's focus return.
   */
  modal?: "preview" | "submit";
} = {}) {
  const projectId = useProjectId();
  const overview = useProjectOverviewQuery(projectId);

  /**
   * Rendered on EVERY branch below, the failure ones included: a clinician at
   * `/preview` with an Overview that cannot load still asked for the preview
   * modal, and the modal states its own loading and failure honestly.
   *
   * ALWAYS THE FIRST CHILD of whatever this component returns. The branches
   * below swap their content as the overview query settles, and a dialog
   * whose position in the tree moved with them would be UNMOUNTED and
   * remounted by that swap — dropping the modal's own state and focus
   * mid-read. Position 0 of the fragment is the same slot in every branch,
   * so the dialog lives through the page beneath it loading.
   */
  const overlay =
    modal === "preview" ? (
      <ProjectPreviewDialog projectId={projectId} />
    ) : modal === "submit" ? (
      <ProjectSubmitDialog projectId={projectId} />
    ) : null;

  if (overview.isPending) {
    return (
      <>
        {overlay}
        <QueryPendingState
          isPaused={overview.isPaused}
          failureReason={overview.failureReason}
          label="Loading project overview"
          errorTitle="Overview could not be loaded"
          rows={3}
          queryKey={queryKeys.projectOverview(projectId)}
        />
      </>
    );
  }

  // Transient refresh errors retain the frame; terminal errors remove cached evidence.
  if (
    !overview.data ||
    (overview.isError && isTerminalQueryError(overview.error))
  ) {
    return (
      <>
        {overlay}
        <QueryErrorState
          error={overview.error}
          subject="Overview cards"
          title="Overview could not be loaded"
          onRetry={() => void overview.refetch()}
        />
      </>
    );
  }

  const { draft, lifecycle } = overview.data;
  const validation = draft.validationState
    ? VALIDATION_LABEL[draft.validationState]
    : undefined;

  return (
    <>
      {overlay}
      <div className="space-y-6">
        <StaleDataNotice
          visible={overview.isError}
          updatedAt={overview.dataUpdatedAt}
          onRetry={() => void overview.refetch()}
        />

        <ProjectJourney projectId={projectId} />
        <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
          <div className="min-w-0 space-y-6">
            <section
              aria-labelledby="frozen-evidence-heading"
              className="space-y-2"
            >
              <SectionLabel id="frozen-evidence-heading">
                Frozen version
              </SectionLabel>
              <Card>
                <CardBody>
                  <PublicationLane
                    projectId={projectId}
                    status={overview.data.publicationStatus}
                    candidate={draft.latestCandidate}
                    lifecycle={lifecycle}
                    detailsOnly
                  />
                </CardBody>
              </Card>
            </section>

            <Card>
              <CardHeader>
                <CardTitle>
                  {draft.present ? "Draft in progress" : "No draft in progress"}
                </CardTitle>
              </CardHeader>
              <CardBody>
                {draft.present ? (
                  <div>
                    <dl className="divide-y divide-border">
                      <DefinitionRow term="Last edited">
                        {formatDateTime(draft.updatedAt)}
                      </DefinitionRow>
                      <DefinitionRow term="Validation">
                        {validation ? (
                          <Badge tone={validation.tone}>
                            {validation.label}
                          </Badge>
                        ) : (
                          EM_DASH
                        )}
                      </DefinitionRow>
                    </dl>
                    <details className="pt-3">
                      <summary className="cursor-pointer text-sm text-muted-foreground">
                        Draft technical details
                      </summary>
                      <dl>
                        <DefinitionRow term="Latest candidate">
                          <span className="break-all font-mono text-xs">
                            {draft.latestCandidateId ?? EM_DASH}
                          </span>
                        </DefinitionRow>
                      </dl>
                    </details>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    Configuration changes start here. The release journey above
                    shows the review and deployment status of the accepted version.
                  </p>
                )}
              </CardBody>
            </Card>
          </div>

          <ProjectHistoryCard projectId={projectId} />
        </div>
      </div>
    </>
  );
}
